import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { formatDateTime, formatPct, truncate } from './historyFormatters';
import {
  tableSx,
  headSx,
  groupHeadSx,
  numSx,
  valueSx,
  dividerLeftSx,
  subHeadStickySx,
  tooltipPopperProps,
} from './historyTableStyles';

const hrsText = (hrs) => {
  if (hrs == null) return '—';
  const n = Number(hrs);
  return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)} hrs`;
};

/** A stacked cell: large percent over small hours; optional change highlight. */
function PctHrsCell({ pct, hrs, changed = false, divider = false }) {
  return (
    <TableCell
      sx={{
        ...numSx,
        ...(divider ? dividerLeftSx : {}),
        ...(changed
          ? {
              bgcolor: 'var(--phm-accent-bg)',
              color: 'var(--phm-accent-text)',
            }
          : {}),
      }}
    >
      <div className="phm-pct-value">{formatPct(pct)}</div>
      <div className="phm-pct-hrs">{hrsText(hrs)}</div>
    </TableCell>
  );
}

/**
 * BudgetSplitTable — two-row header grouping Shop/Field "Before" vs "After".
 * Each percentage cell shows the percent (large) over the hours (small).
 * "After" cells whose value changed from "before" are highlighted. The first
 * entry (initial budget) has null "before" values shown as em-dashes.
 * Newest first (from API).
 */
function BudgetSplitTable({ rows = [] }) {
  if (!rows.length) {
    return <div className="phm-empty">No records found.</div>;
  }

  return (
    <Table size="small" stickyHeader sx={tableSx}>
      <TableHead>
        <TableRow>
          <TableCell sx={headSx} rowSpan={2}>
            Date &amp; Time
          </TableCell>
          <TableCell sx={headSx} rowSpan={2}>
            Changed by
          </TableCell>
          <TableCell sx={groupHeadSx} colSpan={2}>
            Before
          </TableCell>
          <TableCell sx={{ ...groupHeadSx, ...dividerLeftSx }} colSpan={2}>
            After
          </TableCell>
          <TableCell sx={headSx} rowSpan={2}>
            Notes
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={{ ...headSx, ...numSx, ...subHeadStickySx }}>
            Shop %
          </TableCell>
          <TableCell sx={{ ...headSx, ...numSx, ...subHeadStickySx }}>
            Field %
          </TableCell>
          <TableCell
            sx={{ ...headSx, ...numSx, ...dividerLeftSx, ...subHeadStickySx }}
          >
            Shop %
          </TableCell>
          <TableCell sx={{ ...headSx, ...numSx, ...subHeadStickySx }}>
            Field %
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => {
          const shopChanged =
            r.oldShopBudgetHrs != null &&
            Number(r.newShopBudgetHrs) !== Number(r.oldShopBudgetHrs);
          const fieldChanged =
            r.oldFieldBudgetHrs != null &&
            Number(r.newFieldBudgetHrs) !== Number(r.oldFieldBudgetHrs);
          return (
            <TableRow key={r.id}>
              <TableCell sx={valueSx}>{formatDateTime(r.changedAt)}</TableCell>
              <TableCell sx={valueSx}>{r.changedByName || '—'}</TableCell>
              <PctHrsCell pct={r.oldShopPct} hrs={r.oldShopBudgetHrs} />
              <PctHrsCell pct={r.oldFieldPct} hrs={r.oldFieldBudgetHrs} />
              <PctHrsCell
                pct={r.newShopPct}
                hrs={r.newShopBudgetHrs}
                changed={shopChanged}
                divider
              />
              <PctHrsCell
                pct={r.newFieldPct}
                hrs={r.newFieldBudgetHrs}
                changed={fieldChanged}
              />
              <TableCell sx={{ color: 'var(--phm-text-value)' }}>
                {r.notes ? (
                  r.notes.length > 30 ? (
                    <Tooltip title={r.notes} PopperProps={tooltipPopperProps}>
                      <span>{truncate(r.notes)}</span>
                    </Tooltip>
                  ) : (
                    r.notes
                  )
                ) : (
                  '—'
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export default BudgetSplitTable;
